import { themeMode } from '@juvare/common-web-ui/JuvareTheme';
import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import GoogleIcon from '@mui/icons-material/Google';
import { FormControl } from '@mui/material';
import React from 'react';
import { buildEventTab, buildGeneralTab, buildHiddenTab, buildStyleTab, showWidgetEditDialog } from '../WidgetEditDialog';
import { FormHelperErrorDescription } from '../WidgetForm/Components';
import { defaultWidgetLabelProvider, escapeHtml, getVisibleError, handleWidgetUpdate, isSkipValidate, makeNewConfigUnique, renderForm, validateCustom, } from '../WidgetForm/Utils';
import { type } from './types';
const WidgetFormGoogleReCaptchaV2 = (props) => {
    var _a, _b;
    const ref = React.useRef(null);
    const isLoaded = React.useRef(false);
    const error = getVisibleError(props);
    const value = props.getDataStringOrUndefined();
    const setData = React.useCallback((response) => {
        props.setDataStringOrUndefined(response);
        renderForm(props, { changed: true, autoSaveDebounce: props.config.autoSave });
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const siteKey = (_b = (_a = props.rootParams.globalWidgetConfig) === null || _a === void 0 ? void 0 : _a.googleRecaptchaV2SiteKey) !== null && _b !== void 0 ? _b : props.config.siteKey;
    const handleRenderReCaptcha = React.useCallback(() => {
        if (window.grecaptcha) {
            grecaptcha.ready(() => {
                if (ref.current && !isLoaded.current) {
                    if (value) {
                        setData();
                    }
                    grecaptcha.render(ref.current, {
                        sitekey: siteKey,
                        theme: themeMode === 'light' ? 'light' : 'dark',
                        callback: (response) => {
                            setData(response);
                        },
                        'expired-callback': () => {
                            setData();
                        },
                        'error-callback': () => {
                            setData();
                        },
                    });
                    isLoaded.current = true;
                }
            });
        }
    }, [value, setData, siteKey]);
    React.useEffect(() => {
        const scriptUrl = 'https://www.google.com/recaptcha/api.js?render=explicit';
        let script = document.getElementById('GoogleReCaptchaV2');
        if (!script) {
            script = document.createElement('script');
            script.src = scriptUrl;
            script.id = 'GoogleReCaptchaV2';
            document.body.appendChild(script);
            script.onload = () => handleRenderReCaptcha();
        }
        else {
            handleRenderReCaptcha();
        }
    }, [handleRenderReCaptcha]);
    return (React.createElement(FormControl, { fullWidth: true, variant: 'outlined', required: true, error: !!error },
        React.createElement("div", { ref: ref }),
        React.createElement(FormHelperErrorDescription, { error: error, description: props.config.description })));
};
const WidgetFormGoogleReCaptchaV2Editor = (props) => {
    var _a;
    const siteKey = (_a = props.rootParams.globalWidgetConfig) === null || _a === void 0 ? void 0 : _a.googleRecaptchaV2SiteKey;
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props, { widgetLabelProvider: defaultWidgetLabelProvider, onEditWidget: (endEditing) => showWidgetEditDialog(props, endEditing, { title: 'GoogleReCaptchaV2: ' + props.config.label }, buildGeneralTab(props, {
            key: true,
            label: true,
            description: true,
            children: [
                ...(siteKey
                    ? [
                        {
                            type: 'form.content',
                            html: `<p>Site key: ${escapeHtml(siteKey)}</p>`,
                        },
                    ]
                    : [
                        {
                            type: 'form.text',
                            key: 'siteKey',
                            label: 'Site Key',
                            validate: {
                                required: true,
                            },
                        },
                    ]),
            ],
        }), buildHiddenTab(props), buildStyleTab(props), buildEventTab(props, { eventOnChange: true, autoSave: true })) }),
        React.createElement(WidgetFormGoogleReCaptchaV2, Object.assign({}, props))));
};
export const widgetFormGoogleReCaptchaV2 = {
    type,
    validate: (props) => {
        if (isSkipValidate(props)) {
            return;
        }
        const value = props.getDataStringOrUndefined();
        if (!value) {
            return props.config.label ? props.config.label + ' is required' : 'Required';
        }
        return validateCustom(props, value);
    },
    update: handleWidgetUpdate,
    component: WidgetFormGoogleReCaptchaV2,
    icon: React.createElement(GoogleIcon, { fontSize: 'small' }),
    editorComponent: WidgetFormGoogleReCaptchaV2Editor,
    createWidget: (rootConfig, editorPath, widgets) => makeNewConfigUnique(rootConfig, editorPath, widgets, { type, key: 'captcha', label: 'GoogleReCaptchaV2', siteKey: '' }),
};
